import { API_CONFIG } from "@/src/config/api";

export type ApiClientOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  headers?: HeadersInit;
  signal?: AbortSignal;
};

export class ApiClientError extends Error {
  public readonly status: number;

  public constructor(message: string, status: number) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
  }
}

const buildUrl = (path: string): string => {
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;

  if (API_CONFIG.baseUrl.startsWith("http://") || API_CONFIG.baseUrl.startsWith("https://")) {
    return `${API_CONFIG.baseUrl}${normalizedPath}`;
  }

  return `${API_CONFIG.baseUrl}${normalizedPath}`;
};

const withTimeout = (signal?: AbortSignal): AbortSignal => {
  if (signal) {
    return signal;
  }

  const controller = new AbortController();
  setTimeout(() => controller.abort(), API_CONFIG.timeoutMs);
  return controller.signal;
};

async function request<T>(path: string, options: ApiClientOptions = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(buildUrl(path), {
      method: options.method ?? "GET",
      body: options.body ? JSON.stringify(options.body) : undefined,
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      signal: withTimeout(options.signal),
      cache: "no-store",
    });
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      throw new ApiClientError("The request timed out. Please try again.", 408);
    }

    if (error instanceof Error) {
      throw new ApiClientError("Unable to reach the server. Please try again.", 0);
    }

    throw new ApiClientError("Request failed", 0);
  }

  if (!response.ok) {
    let message = "Request failed";
    try {
      const payload = (await response.json()) as { message?: string };
      message = payload.message ?? message;
    } catch {
      message = response.statusText || message;
    }
    throw new ApiClientError(message, response.status);
  }

  return (await response.json()) as T;
}

export const apiClient = {
  get: <T>(path: string, options?: Omit<ApiClientOptions, "method" | "body">) =>
    request<T>(path, { ...options, method: "GET" }),
  post: <T>(path: string, body: unknown, options?: Omit<ApiClientOptions, "method" | "body">) =>
    request<T>(path, { ...options, method: "POST", body }),
  put: <T>(path: string, body: unknown, options?: Omit<ApiClientOptions, "method" | "body">) =>
    request<T>(path, { ...options, method: "PUT", body }),
  patch: <T>(path: string, body: unknown, options?: Omit<ApiClientOptions, "method" | "body">) =>
    request<T>(path, { ...options, method: "PATCH", body }),
  delete: <T>(path: string, options?: Omit<ApiClientOptions, "method" | "body">) =>
    request<T>(path, { ...options, method: "DELETE" }),
};
